import { type RefObject, useCallback, useEffect, useRef } from 'react'
import {
  getTimeSeriesRectangularClipboardSelection,
  type TimeSeriesTableClipboardCellPosition,
} from '../TimeSeriesDataTable.clipboard.adapter'
import { hasSameSelection } from '../TimeSeriesDataTable.helper'

type UseClipboardDragSelectionParams = {
  selectionAnchorRef: RefObject<TimeSeriesTableClipboardCellPosition | null>
  setSelectedCells: React.Dispatch<React.SetStateAction<Set<string>>>
}

type UseClipboardDragSelectionReturn = {
  queueDragSelectionUpdate: (nextCell: TimeSeriesTableClipboardCellPosition) => void
  clearPendingDragSelection: () => void
  getSelectionForCopy: (selectedCells: Set<string>) => Set<string>
}

export function useRafThrottledDragSelection({
  selectionAnchorRef,
  setSelectedCells,
}: UseClipboardDragSelectionParams): UseClipboardDragSelectionReturn {
  const dragSelectionRafRef = useRef<number | null>(null)
  const pendingDragCellRef = useRef<TimeSeriesTableClipboardCellPosition | null>(null)

  const clearPendingDragSelection = useCallback((): void => {
    if (dragSelectionRafRef.current !== null) {
      window.cancelAnimationFrame(dragSelectionRafRef.current)
      dragSelectionRafRef.current = null
    }
    pendingDragCellRef.current = null
  }, [])

  const queueDragSelectionUpdate = useCallback(
    (nextCell: TimeSeriesTableClipboardCellPosition): void => {
      const selectionAnchor = selectionAnchorRef.current
      if (!selectionAnchor) {
        return
      }

      pendingDragCellRef.current = nextCell
      if (dragSelectionRafRef.current !== null) {
        return
      }

      dragSelectionRafRef.current = window.requestAnimationFrame(() => {
        dragSelectionRafRef.current = null
        const pendingCell = pendingDragCellRef.current
        pendingDragCellRef.current = null
        const anchor = selectionAnchorRef.current

        if (!anchor || !pendingCell) {
          return
        }

        setSelectedCells((previousSelection) => {
          const nextSelection = getTimeSeriesRectangularClipboardSelection(anchor, pendingCell)
          return hasSameSelection(previousSelection, nextSelection) ? previousSelection : nextSelection
        })
      })
    },
    [selectionAnchorRef, setSelectedCells]
  )

  const getSelectionForCopy = useCallback(
    (selectedCells: Set<string>): Set<string> => {
      const pendingDragCell = pendingDragCellRef.current
      const selectionAnchor = selectionAnchorRef.current
      if (selectionAnchor && pendingDragCell) {
        return getTimeSeriesRectangularClipboardSelection(selectionAnchor, pendingDragCell)
      }

      return selectedCells
    },
    [selectionAnchorRef]
  )

  useEffect(() => {
    return () => {
      if (dragSelectionRafRef.current !== null) {
        window.cancelAnimationFrame(dragSelectionRafRef.current)
      }
    }
  }, [])

  return {
    queueDragSelectionUpdate,
    clearPendingDragSelection,
    getSelectionForCopy,
  }
}
